import {
  Badge,
  Box,
  Button,
  Flex,
  Hide,
  Slide,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import rootStore from '../../../store/RootStore'
import { observer } from 'mobx-react'

import Dropdown from '../../../components/ui/Dropdown/Dropdown'
import { getChartActions } from '../../../utils/ChartHelper'
import { ReportContext } from '../../../contexts/ReportContext'
import { ExportHelper } from '../../../utils/ExportHelper'
import ReactGA from 'react-ga4'

const exportOptions = filters =>
  getChartActions({}).map(item => ({
    label: item.label,
    id: item.id,
    value: item.value,
    filters,
  }))
/**
 * Why a ticked chart has no live instance to export, in words that are true of the case at hand.
 * Two things produce one: a chart that has left the page since it was ticked — a filter change
 * inside a componentType-3 group, or a search — and a table, whose branch of `Chart` renders no
 * Highcharts at all while `ChartHeader` still offers it a download checkbox. Blaming unmounting for
 * both told a reader looking straight at their tables that they were no longer on the page.
 *
 * Making tables exportable is out of scope: every adapter dereferences `chartElement.chart`.
 */
const skippedDescription = skipped => {
  const tables = skipped.filter(
    ({ chart }) => chart?.chartOptions?.chart?.type === 'table'
  ).length

  if (tables === 0) {
    return 'Charts that have left the page since you selected them cannot be exported. Select them again to include them.'
  }
  if (tables === skipped.length) {
    return 'Tables cannot be exported, only charts can.'
  }
  return 'Tables cannot be exported, and the rest have left the page since you selected them.'
}

export const DownloadFooter = observer(() => {
  const {
    reportStore: {
      downloadSelection,
      resetDownloadSelection,
      filterOptionsSelected,
    },
    chartStore: { charts, getChartInstance },
  } = rootStore
  const filters = useMemo(
    () => [
      [...filterOptionsSelected.values()].map(filter => {
        return filter.label
      }),
    ],
    [filterOptionsSelected],
  )

  const reportContext = useContext(ReportContext)
  const toast = useToast()

  const [format, setFormat] = useState('pdf')

  const downloadAllCharts = useCallback(async () => {
    // The adapters read the live Highcharts wrapper off `chartElement`, which the registry holds
    // rather than the chart data. A ticked chart can have no instance to hand over, and nothing
    // prunes it from the selection; `batchExportPPTX` then dereferences `.chart` on the data
    // object it falls back to and throws uncaught, so the Export button does nothing at all.
    const selection = downloadSelection.map(id => ({
      id,
      chart: charts[id],
      chartElement: getChartInstance(id),
    }))
    const exportable = selection.filter(({ chartElement }) =>
      Boolean(chartElement)
    )
    const skipped = selection.filter(({ chartElement }) => !chartElement)

    if (exportable.length === 0) {
      toast({
        title: 'Nothing to export',
        description: skippedDescription(skipped),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    if (skipped.length > 0) {
      toast({
        title: `Exporting ${exportable.length} of ${selection.length} charts`,
        description: skippedDescription(skipped),
        status: 'info',
        duration: 5000,
        isClosable: true,
      })
    }

    const selectedCharts = exportable.map(({ chart, chartElement }) => ({
      ...chart,
      chartElement,
    }))
    const exportHelper = new ExportHelper(format, selectedCharts, filters, '')
    // Awaited and caught: the adapters can fail on a wrapper whose Highcharts instance has been
    // destroyed, or inside jspdf/pptxgenjs, and unhandled that produced no file and no message —
    // the same 'the button does nothing' symptom the selection filter above was written to remove.
    try {
      await exportHelper.export()
    } catch (error) {
      console.error(error)
      toast({
        title: 'Export failed',
        description:
          'The file could not be produced. Try again, or export fewer charts at once.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }
    ReactGA.event({
      category: 'download',
      action: 'download',
      label: `${exportable.map(({ id }) => id).join(',')}/${filters.join(',')}`,
    })
  }, [charts, downloadSelection, filters, format, getChartInstance, toast])

  const handleDownloadClose = useCallback(() => {
    resetDownloadSelection()
    reportContext.onDownloadClose()
  }, [reportContext, resetDownloadSelection])

  const handleFormatChange = ({ value }) => {
    setFormat(value)
  }
  const slideStyles = useBreakpointValue({
    base: {
      zIndex: 2,
    },
    xl: {
      zIndex: 1000,
    },
  })
  return (
    <Slide
      direction="bottom"
      in={reportContext.isDownloadOpen}
      style={slideStyles}>
      <Flex {...flexStyles}>
        <Flex {...innerFlexStyles}>
          <Flex>
            <Badge
              px={2}
              mr={2}
              height={'100%'}
              fontSize="1.1em"
              variant="solid"
              colorScheme="blackAlpha"
              bgColor={'#444444'}
              borderRadius={7}
              fontWeight={400}>
              {downloadSelection?.length}
            </Badge>
            Select chart(s) / data to export
          </Flex>
          <Hide below="sm">
            <Box minWidth={{ base: 0, sm: 150 }}>
              <Dropdown
                items={exportOptions(filters)}
                onChange={handleFormatChange}
                shouldUsePortal={false}
              />
            </Box>
          </Hide>
          <Flex
            alignItems="center"
            justifyContent="space-between">
            <Button
              colorScheme="orange"
              minWidth={150}
              onClick={downloadAllCharts}
              disabled={downloadSelection?.length === 0}>
              Export selected
            </Button>
            <Button
              colorScheme="blackAlpha"
              maxW={150}
              ml={'16px'}
              onClick={handleDownloadClose}>
              Cancel
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Slide>
  )
})

const flexStyles = {
  height: { base: '120px', lg: '90px' },
  justify: 'center',
  alignItems: 'center',
  w: '100vw',
  bg: 'white',
  p: '12px 32px',
  boxShadow: '0 -3px 4px rgba(0, 0, 0, .03)',
}
const innerFlexStyles = {
  flexDirection: { base: 'column', lg: 'row' },
  alignItems: 'center',
  height: '100%',
  justifyContent: { base: 'space-around', xl: 'space-between' },
  w: { base: '100%', xl: '3xl' },
}

DownloadFooter.displayName = 'DownloadFooter'

DownloadFooter.propTypes = {
  isDownloadOpen: PropTypes.bool,
  downloadAllCharts: PropTypes.func,
  onDownloadClose: PropTypes.func,
  downloadCount: PropTypes.any,
}
